import { useState } from 'react';
import { IconChevronRight, IconDots } from '@rossum/ui/icons/tabler';
import {
  Box,
  Link,
  Menu,
  MenuItem,
  Breadcrumbs as MUIBreadcrumbs,
  SvgIcon,
  Tooltip,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { useComponentsContext, useIntl } from '../ComponentsContext';
import { Breadcrumb } from './Breadcrumb';

export type BreadcrumbItem = {
  label: string | JSX.Element;
  to?: string;
};

type BreadcrumbsProps = {
  breadcrumbs: readonly [BreadcrumbItem, ...BreadcrumbItem[]];
};

export const Breadcrumbs = ({ breadcrumbs }: BreadcrumbsProps) => {
  const intl = useIntl();
  const { LinkComponent } = useComponentsContext();
  const [anchorEl, setAnchorEl] = useState<HTMLSpanElement | null>(null);

  const breadcrumbsAreCollapsed = breadcrumbs.length > 4;

  if (breadcrumbsAreCollapsed) {
    const firstItem = breadcrumbs[0];
    const hiddenItems = breadcrumbs.slice(1, -2);
    const lastItems = breadcrumbs.slice(-2);

    return (
      <MUIBreadcrumbs
        separator={
          <SvgIcon fontSize="small" color="disabled">
            <IconChevronRight />
          </SvgIcon>
        }
      >
        <Breadcrumb key="first" to={firstItem.to}>
          {firstItem.label}
        </Breadcrumb>
        <Breadcrumb>
          <Tooltip
            placement="top"
            title={intl.formatMessage(
              {
                id: 'component.breadcrumbs.tooltip',
              },
              {
                count: hiddenItems.length,
              }
            )}
          >
            <Box
              component="span"
              onClick={e => setAnchorEl(e.currentTarget)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: borderRadius('s'),
                backgroundColor: t =>
                  anchorEl ? t.palette.action.selected : 'transparent',
                transition: t => t.transitions.create(['background-color']),
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: t => t.palette.action.hover,
                },
              }}
            >
              <SvgIcon fontSize="medium">
                <IconDots />
              </SvgIcon>
            </Box>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={() => setAnchorEl(null)}
          >
            {hiddenItems.map((item, index) => (
              <MenuItem
                disabled={!item.to}
                // oxlint-disable-next-line react/no-array-index-key -- oxlint stricter than eslint — index used as fallback, not sole key
                key={item.to ?? index}
                onClick={() => {
                  setAnchorEl(null);
                }}
              >
                <Link
                  sx={{
                    color: 'inherit',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'none',
                    },
                  }}
                  component={LinkComponent}
                  to={item.to ?? ''}
                >
                  {item.label}
                </Link>
              </MenuItem>
            ))}
          </Menu>
        </Breadcrumb>
        {lastItems.map(({ label, to }, index) => (
          // oxlint-disable-next-line react/no-array-index-key -- oxlint stricter than eslint — index used as fallback, not sole key
          <Breadcrumb key={to ?? index} to={to}>
            {label}
          </Breadcrumb>
        ))}
      </MUIBreadcrumbs>
    );
  }

  return (
    <MUIBreadcrumbs
      separator={
        <SvgIcon fontSize="small" color="disabled">
          <IconChevronRight />
        </SvgIcon>
      }
    >
      {breadcrumbs.map(({ label, to }, index) => (
        // oxlint-disable-next-line react/no-array-index-key -- oxlint stricter than eslint — index used as fallback, not sole key
        <Breadcrumb key={to ?? index} to={to}>
          {label}
        </Breadcrumb>
      ))}
    </MUIBreadcrumbs>
  );
};
